/**
 * Org Templates library tab, routed through the same Document Library chrome as
 * the Documents and Knowledge Base tabs: the FilterBar (breadcrumb, tab
 * switcher, inline search, explainer banner, Upload-template button) over the
 * compile-status list, with the row-click detail pane mounted through
 * DocumentDetailLayout's resizable split-pane. Owns only the templates state
 * (search, selection, upload dialog); the layout owns the panel choreography.
 */
import { useState } from "react";
import { useDebouncedEffect, useMediaQuery } from "@react-hookz/web";
import type { PaginationState } from "@tanstack/react-table";
import { SearchX } from "lucide-react";

import { DocumentDropZone } from "@/components/frontend/DocumentDropZone";
import { DocumentDetailLayout } from "@/components/frontend/documents/DocumentDetailLayout";
import { Button } from "@/components/ui/button";

import { FilterBar } from "../FilterBar";
import { TemplateDetailPanel } from "./TemplateDetailPanel";
import { TemplateList } from "./TemplateList";
import { TemplatesEmptyState } from "./TemplatesEmptyState";
import { UploadTemplateDialog } from "./UploadTemplateDialog";
import {
    TEMPLATE_ACCEPT,
    TEMPLATE_INVALID_TYPE_DESCRIPTION,
    TEMPLATE_MAX_FILE_BYTES,
} from "./templateUploadConfig";
import { useOutputTemplates, useRetryCompile } from "./useOutputTemplates";

const SEARCH_DEBOUNCE_MS = 250;

export function TemplatesTab() {
    const isMobile = useMediaQuery("(max-width: 768px)") ?? false;
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    useDebouncedEffect(
        () => {
            setDebouncedSearch(search);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
        },
        [search],
        SEARCH_DEBOUNCE_MS
    );
    const [uploadOpen, setUploadOpen] = useState(false);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
        null
    );

    const { data, isLoading, isError } = useOutputTemplates(
        debouncedSearch,
        pagination
    );
    const retryCompile = useRetryCompile();
    const templates = data?.items ?? [];

    const closePanel = () => setSelectedTemplateId(null);
    const handleDropFiles = (files: File[]) => {
        setDroppedFiles(files);
        setUploadOpen(true);
    };
    const closeUpload = () => {
        setUploadOpen(false);
        setDroppedFiles([]);
    };

    return (
        <>
            <DocumentDropZone
                title="Upload spreadsheet or PDF templates here"
                subtitle="Drop Excel or PDF files here"
                onDropFiles={handleDropFiles}
                disabled={uploadOpen}
                accept={TEMPLATE_ACCEPT}
                maxSize={TEMPLATE_MAX_FILE_BYTES}
                invalidTypeDescription={TEMPLATE_INVALID_TYPE_DESCRIPTION}
                className="-my-4 -ml-4 flex min-h-0 flex-1 flex-col py-4 pl-4"
            >
                <DocumentDetailLayout
                    isMobile={isMobile}
                    selectedDocumentId={selectedTemplateId}
                    onClose={closePanel}
                    renderPanel={(args) =>
                        args.id === null ? null : (
                            <TemplateDetailPanel
                                open={args.open}
                                onClose={args.onClose}
                                templateId={args.id}
                                isMobile={args.isMobile}
                            />
                        )
                    }
                >
                    {() => (
                        <>
                            <FilterBar
                                activeTab="templates"
                                showTemplatesTab
                                searchValue={search}
                                onSearchChange={setSearch}
                                onSearchClear={() => setSearch("")}
                                typeFilter="all"
                                onUploadClick={() => setUploadOpen(true)}
                                currentFolder={null}
                                folders={[]}
                            />

                            <div className="flex min-h-0 flex-1 flex-col">
                                {isError ? (
                                    <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                                        Couldn&apos;t load templates. Refresh to
                                        try again.
                                    </div>
                                ) : isLoading ? (
                                    <TemplateListSkeleton />
                                ) : (
                                    <TemplateList
                                        templates={templates}
                                        rowCount={data?.total ?? 0}
                                        pagination={pagination}
                                        onPaginationChange={setPagination}
                                        onRetry={(id) =>
                                            retryCompile.mutate(id)
                                        }
                                        onRowClick={setSelectedTemplateId}
                                        emptyState={
                                            debouncedSearch.trim() ? (
                                                <TemplatesNoResults
                                                    query={debouncedSearch}
                                                    onClear={() =>
                                                        setSearch("")
                                                    }
                                                />
                                            ) : (
                                                <TemplatesEmptyState
                                                    onUpload={() =>
                                                        setUploadOpen(true)
                                                    }
                                                />
                                            )
                                        }
                                    />
                                )}
                            </div>
                        </>
                    )}
                </DocumentDetailLayout>
            </DocumentDropZone>

            <UploadTemplateDialog
                open={uploadOpen}
                onClose={closeUpload}
                initialFiles={
                    droppedFiles.length > 0 ? droppedFiles : undefined
                }
            />
        </>
    );
}

function TemplatesNoResults({
    query,
    onClear,
}: {
    query: string;
    onClear: () => void;
}) {
    const truncated = query.length > 40 ? `${query.slice(0, 40)}...` : query;
    return (
        <div
            data-testid="templates-no-results"
            className="flex min-h-64 items-center justify-center px-4 py-12"
        >
            <div className="flex flex-col items-center justify-center gap-3 text-center">
                <SearchX
                    className="text-muted-foreground/40 size-12"
                    aria-hidden
                />
                <div className="max-w-md">
                    <p className="text-sm font-medium">
                        No results for &ldquo;{truncated}&rdquo;
                    </p>
                    <p className="text-muted-foreground mt-0.5 max-w-md text-xs">
                        Try different keywords or clear your search.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={onClear}>
                    Clear search
                </Button>
            </div>
        </div>
    );
}

function TemplateListSkeleton() {
    return (
        <div
            data-testid="templates-loading"
            className="overflow-hidden rounded-lg border"
        >
            {Array.from({ length: 4 }).map((_, i) => (
                <div
                    key={i}
                    className="flex items-center gap-3 border-b p-4 last:border-b-0"
                >
                    <div className="bg-muted size-8 shrink-0 animate-pulse rounded-full" />
                    <div className="flex flex-1 flex-col gap-1.5">
                        <div className="bg-muted h-3 w-1/3 animate-pulse rounded" />
                        <div className="bg-muted/70 h-2.5 w-1/5 animate-pulse rounded" />
                    </div>
                </div>
            ))}
        </div>
    );
}
